import React, { useState, useEffect } from 'react';
import { Wallet } from 'lucide-react';
import { SEO } from '../components/SEO';

interface PensionState {
  monthlyIncome: string;
  yearsOfService: string;
  age: string;
  gender: 'M' | 'F';
  country: 'HR' | 'BA' | 'RS';
}

const countryConfigs = {
  HR: {
    currency: 'EUR',
    symbol: '€',
    minYears: 15,
    minAge: {
      M: 65,
      F: 63
    },
    baseRate: 0.55, // Base pension rate
    yearlyIncrease: 0.01, // Increase per year over minimum
    maxRate: 0.85 // Maximum pension rate
  },
  BA: {
    currency: 'BAM',
    symbol: 'KM',
    minYears: 15,
    minAge: {
      M: 65,
      F: 65
    },
    baseRate: 0.45,
    yearlyIncrease: 0.015,
    maxRate: 0.75
  },
  RS: {
    currency: 'RSD',
    symbol: 'RSD',
    minYears: 15,
    minAge: {
      M: 65,
      F: 63
    },
    baseRate: 0.50,
    yearlyIncrease: 0.012,
    maxRate: 0.80
  }
};

function IzracunMirovine() {
  const [values, setValues] = useState<PensionState>({
    monthlyIncome: '',
    yearsOfService: '',
    age: '',
    gender: 'M',
    country: 'HR'
  });

  const [results, setResults] = useState<{
    estimatedPension: number;
    replacementRate: number;
    yearsUntilRetirement: number | null;
    isEligible: boolean;
    message: string;
  } | null>(null);

  const calculatePension = () => {
    const config = countryConfigs[values.country];
    const income = parseFloat(values.monthlyIncome);
    const years = parseFloat(values.yearsOfService);
    const age = parseFloat(values.age);
    
    if (isNaN(income) || isNaN(years) || isNaN(age)) {
      return null;
    }

    const minRetirementAge = config.minAge[values.gender];
    const yearsUntilRetirement = Math.max(0, minRetirementAge - age);
    const isEligible = years >= config.minYears && age >= minRetirementAge;

    // Calculate pension rate based on years of service
    let pensionRate = config.baseRate;
    const extraYears = Math.max(0, years - config.minYears);
    pensionRate += extraYears * config.yearlyIncrease;
    pensionRate = Math.min(pensionRate, config.maxRate);

    // Calculate estimated pension
    const estimatedPension = income * pensionRate;
    const replacementRate = pensionRate * 100;

    let message = '';
    if (!isEligible) {
      if (years < config.minYears) {
        message = `Potrebno je još ${config.minYears - years} godina staža za mirovinu.`;
      }
      if (age < minRetirementAge) {
        message += ` Potrebno je još ${yearsUntilRetirement} godina do mirovinske dobi.`;
      }
    } else {
      message = 'Ispunjavate uvjete za mirovinu.';
    }

    return {
      estimatedPension: parseFloat(estimatedPension.toFixed(2)),
      replacementRate: parseFloat(replacementRate.toFixed(1)),
      yearsUntilRetirement: yearsUntilRetirement > 0 ? yearsUntilRetirement : null,
      isEligible,
      message: message.trim()
    };
  };

  const handleInputChange = (field: keyof PensionState, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  useEffect(() => {
    if (values.monthlyIncome && values.yearsOfService && values.age) {
      const results = calculatePension();
      setResults(results);
    }
  }, [values]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('hr-HR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Izračun Mirovine",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte očekivanu mirovinu i starosne uvjete. Precizni izračuni za Hrvatsku, BiH i Srbiju.",
    "url": "https://online-kalkulator.com/izracun-mirovine",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Izračun očekivane mirovine",
      "Podrška za više država",
      "Izračun starosnih uvjeta",
      "Automatsko ažuriranje",
      "Besplatno korištenje"
    ]
  };

  return (
    <>
      <SEO 
        title="Izračun Mirovine | Kalkulator za Izračun Mirovine"
        description="Izračunajte očekivanu mirovinu i starosne uvjete. Precizni izračuni za Hrvatsku, BiH i Srbiju."
        canonicalUrl="/izracun-mirovine"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Wallet className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator za Izračun Mirovine</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Input Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prosječna mjesečna plaća
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={values.monthlyIncome}
                      onChange={(e) => handleInputChange('monthlyIncome', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={`Unesite iznos u ${countryConfigs[values.country].currency}`}
                    />
                    <span className="absolute right-3 top-2 text-gray-500">
                      {countryConfigs[values.country].symbol}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Godine radnog staža
                  </label>
                  <input
                    type="number"
                    value={values.yearsOfService}
                    onChange={(e) => handleInputChange('yearsOfService', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 35"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Trenutna dob
                  </label>
                  <input
                    type="number"
                    value={values.age}
                    onChange={(e) => handleInputChange('age', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 55"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Spol
                  </label>
                  <select
                    value={values.gender}
                    onChange={(e) => handleInputChange('gender', e.target.value as 'M' | 'F')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="M">Muško</option>
                    <option value="F">Žensko</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Država
                  </label>
                  <select
                    value={values.country}
                    onChange={(e) => handleInputChange('country', e.target.value as 'HR' | 'BA' | 'RS')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="HR">Hrvatska</option>
                    <option value="BA">Bosna i Hercegovina</option>
                    <option value="RS">Srbija</option>
                  </select>
                </div>
              </div>

              {/* Results */}
              {results && (
                <div className="space-y-4 border-t pt-4">
                  <div className="text-center py-2 px-4 rounded-lg bg-gray-50">
                    <p className={`text-lg ${results.isEligible ? 'text-green-600' : 'text-blue-600'}`}>
                      {results.message}
                    </p>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Procjenjena mirovina:</span>
                    <span className="text-xl font-bold text-blue-600">
                      {formatCurrency(results.estimatedPension)} {countryConfigs[values.country].symbol}
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Stopa zamjene:</span>
                    <span className="font-semibold">
                      {results.replacementRate}%
                    </span>
                  </div>

                  {results.yearsUntilRetirement !== null && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Godine do mirovine:</span>
                      <span className="font-semibold">
                        {results.yearsUntilRetirement}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Kalkulatoru Mirovine</h2>
                <p className="text-gray-600 leading-relaxed">
                  Kalkulator mirovine je alat koji vam pomaže procijeniti očekivani iznos mirovine
                  na temelju vaše prosječne plaće, godina radnog staža i drugih faktora. Izračun
                  se temelji na trenutno važećim propisima i može varirati ovisno o državi i
                  specifičnim okolnostima.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Uvjeti za Mirovinu</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Hrvatska</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Minimalno 15 godina radnog staža</li>
                      <li>Starosna dob: 65 godina za muškarce, 63 godine za žene</li>
                      <li>Osnovna stopa: 55% uz povećanje za dodatne godine staža</li>
                      <li>Maksimalna stopa: 85% prosječne plaće</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Bosna i Hercegovina</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Minimalno 15 godina radnog staža</li>
                      <li>Starosna dob: 65 godina za oba spola</li>
                      <li>Osnovna stopa: 45% uz povećanje za dodatne godine staža</li>
                      <li>Maksimalna stopa: 75% prosječne plaće</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Srbija</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Minimalno 15 godina radnog staža</li>
                      <li>Starosna dob: 65 godina za muškarce, 63 godine za žene</li>
                      <li>Osnovna stopa: 50% uz povećanje za dodatne godine staža</li>
                      <li>Maksimalna stopa: 80% prosječne plaće</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Unesite svoju prosječnu mjesečnu plaću</li>
                  <li>Unesite godine radnog staža</li>
                  <li>Unesite trenutnu dob</li>
                  <li>Odaberite spol</li>
                  <li>Odaberite državu</li>
                  <li>Rezultati će se automatski izračunati</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Izračun je informativne prirode</li>
                  <li>Ne uključuje dodatke i posebne okolnosti</li>
                  <li>Stvarni iznos mirovine može odstupati od procjene</li>
                  <li>Konzultirajte se s mirovinskim fondom za točne informacije</li>
                  <li>Izračun se temelji na trenutno važećim propisima</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Faktori koji Utječu na Mirovinu</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Duljina radnog staža</li>
                  <li>Visina prosječne plaće tijekom radnog vijeka</li>
                  <li>Dob odlaska u mirovinu</li>
                  <li>Vrsta mirovine (starosna, prijevremena, invalidska)</li>
                  <li>Dodatni doprinosi i beneficirani staž</li>
                  <li>Zakonske promjene i reforme mirovinskog sustava</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default IzracunMirovine;